import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Building2, Mail, Lock, User, KeyRound, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Er ging iets mis. Probeer het opnieuw.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Auth() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", invite_code: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password, form.invite_code);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#F6F9FB]">
      {/* Left visual (desktop) */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1459679749680-18eb1eb37418?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Amsterdam" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#16202B]/60" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <BrandLogo imgClass="h-14 max-w-[240px] object-contain mb-5" fallback={
            <div className="h-12 w-12 rounded-2xl bg-[#001C51] flex items-center justify-center mb-5">
              <Building2 className="h-6 w-6" strokeWidth={1.75} />
            </div>
          } />
          <h1 className="font-heading font-bold text-4xl leading-tight mb-3">Flex Estate<br />CRM</h1>
          <p className="text-white/80 max-w-sm">Beheer je leads, bel klanten en volg elk gesprek op — in het Nederlands.</p>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-md fe-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <BrandLogo imgClass="max-h-11 max-w-[200px] object-contain" fallback={
              <>
                <div className="h-11 w-11 rounded-xl bg-[#001C51] flex items-center justify-center text-white">
                  <Building2 className="h-6 w-6" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="font-heading font-bold text-xl text-[#16202B]">Flex Estate</p>
                  <p className="text-sm text-[#5B6675] -mt-1">CRM</p>
                </div>
              </>
            } />
          </div>

          <h2 className="font-heading font-bold text-3xl text-[#16202B] mb-1">{mode === "login" ? "Welkom terug" : "Account aanmaken"}</h2>
          <p className="text-[#5B6675] mb-8">{mode === "login" ? "Log in om verder te gaan." : "Maak je account aan met de teamcode."}</p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="text-sm text-[#5B6675] mb-1.5 block">Naam</label>
                <div className="relative">
                  <User className="h-4 w-4 text-[#5B6675] absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input data-testid="auth-name" value={form.name} onChange={set("name")} className="fe-input pl-10" placeholder="Jouw naam" />
                </div>
              </div>
            )}
            <div>
              <label className="text-sm text-[#5B6675] mb-1.5 block">E-mailadres</label>
              <div className="relative">
                <Mail className="h-4 w-4 text-[#5B6675] absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input data-testid="auth-email" type="email" value={form.email} onChange={set("email")} className="fe-input pl-10" placeholder="naam@flexestate.nl" required />
              </div>
            </div>
            <div>
              <label className="text-sm text-[#5B6675] mb-1.5 block">Wachtwoord</label>
              <div className="relative">
                <Lock className="h-4 w-4 text-[#5B6675] absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input data-testid="auth-password" type="password" value={form.password} onChange={set("password")} className="fe-input pl-10" placeholder="••••••••" required />
              </div>
            </div>
            {mode === "register" && (
              <div>
                <label className="text-sm text-[#5B6675] mb-1.5 block">Teamcode</label>
                <div className="relative">
                  <KeyRound className="h-4 w-4 text-[#5B6675] absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input data-testid="auth-invite" value={form.invite_code} onChange={set("invite_code")} className="fe-input pl-10" placeholder="Ontvangen van je collega" />
                </div>
              </div>
            )}

            {error && <p data-testid="auth-error" className="text-sm text-[#B0413E] bg-[#F3DEDD] rounded-xl px-4 py-3">{error}</p>}

            <button data-testid="auth-submit" type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#001C51] text-white font-medium px-4 py-3.5 hover:bg-[#0A337A] transition-colors disabled:opacity-50">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "login" ? "Inloggen" : "Account aanmaken"}
            </button>
          </form>

          <p className="text-center text-sm text-[#5B6675] mt-6">
            {mode === "login" ? "Nog geen account?" : "Heb je al een account?"}{" "}
            <button data-testid="auth-toggle" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="font-semibold text-[#001C51] hover:underline">
              {mode === "login" ? "Registreer met teamcode" : "Log in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
